import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapEntityType, Operation, useLoader } from '../../../../redux';
import { isDateInRange } from '../../../../time';
import { showSuccessToast, showWarningToast } from '../../../../utils';
import { useMapUrlStateContext } from '../../Utils/mapUrlState';
import { useEnsureMapEntityTypeVisible } from '../../Utils/useEnsureMapEntityTypeVisible';
import { CreateChanges } from '../Types';
import { useCreateStop } from './useCreateStop';
import { useDefaultErrorHandler } from './useEditStop';
import { useEnsureTemporalFilterActive } from './useEnsureTemporalFilterActive';
import { useUpdateStopPriorityFilterIfNeeded } from './useUpdateStopPriorityFilterIfNeeded';

export function useCreateStopUtils(onFinishEditing: (netexId: string) => void) {
  const { t } = useTranslation();

  const [createChanges, setCreateChanges] = useState<CreateChanges | null>(
    null,
  );

  const { setIsLoading: setIsLoadingSaveStop } = useLoader(Operation.SaveStop);

  const {
    state: {
      filters: { observationDate },
    },
    setFlatUrlState,
  } = useMapUrlStateContext();

  const updateStopPriorityFilterIfNeeded =
    useUpdateStopPriorityFilterIfNeeded();
  const ensureMapEntityTypeVisible = useEnsureMapEntityTypeVisible();
  const ensureTemporalFilterActive = useEnsureTemporalFilterActive();

  const createStop = useCreateStop();
  const defaultErrorHandler = useDefaultErrorHandler();

  /**
   * Inserts scheduled_stop_point, then inserts stopPlace to tiamat
   * Then updates the scheduled_stop_point's stop_place_ref
   * Note: this might all change if we get a transaction service, but for now
   * this is the way to go.
   */
  const doCreateStop = async (changes: CreateChanges) => {
    setIsLoadingSaveStop(true);
    try {
      const { quayId } = await createStop(changes);
      setCreateChanges(null);

      showSuccessToast(t(($) => $.stops.saveSuccess));

      if (
        !isDateInRange(
          observationDate,
          changes.stopPoint.validity_start,
          changes.stopPoint.validity_end,
        )
      ) {
        setFlatUrlState((p) => ({
          ...p,
          observationDate:
            changes.stopPoint.validity_start ??
            changes.stopPoint.validity_end ??
            observationDate,
        }));
        showWarningToast(t(($) => $.filters.observationDateAdjusted));
      }

      ensureTemporalFilterActive();
      ensureMapEntityTypeVisible(MapEntityType.Stop);
      updateStopPriorityFilterIfNeeded(changes.stopPoint.priority);
      onFinishEditing(quayId);
    } catch (err) {
      defaultErrorHandler(err as Error);
    } finally {
      setIsLoadingSaveStop(false);
    }
  };

  const onCreateStop = async (changes: CreateChanges) => {
    // If conflict, expose them downstream for conflicts modal.
    // Ultimately followed by a call to onCancelCreate
    if (changes.conflicts?.length) {
      setCreateChanges(changes);
    } else {
      await doCreateStop(changes);
    }
  };

  const onCancelCreate = () => setCreateChanges(null);

  return { createChanges, onCreateStop, onCancelCreate };
}
